import type { SystemStyleObject } from '@chakra-ui/react';
import type { IconName } from '@lego/icons';
import { Spinner, theme } from '@lego/klik-ui';
import { type MultiValue as MultiValueType, Select } from 'chakra-react-select';
import { useEffect, useState } from 'react';
import {
  ClearIndicator,
  MultiValue,
  Option,
} from './custom-combobox-components';

export type DefaultOptionType = {
  id: string;
  label: string;
  hasError?: boolean;
  iconName?: IconName;
};

export type ComboboxSize = 'small' | 'medium' | 'large';

// CONNECT standard input/dropdown sizing tokens.
const COMBOBOX_SIZE_STYLES: Record<
  ComboboxSize,
  { minHeight: string; borderRadius: string; padding: string }
> = {
  small: {
    minHeight: 'var(--ds-layout-size-400)',
    borderRadius: 'var(--ds-layout-radius-75)',
    padding: 'var(--ds-layout-spacing-50) var(--ds-layout-spacing-75)',
  },
  medium: {
    minHeight: 'var(--ds-layout-size-500)',
    borderRadius: 'var(--ds-layout-radius-100)',
    padding: '0px var(--ds-layout-spacing-100)',
  },
  large: {
    minHeight: 'var(--ds-layout-size-600)',
    borderRadius: 'var(--ds-layout-radius-150)',
    padding: 'var(--ds-layout-spacing-100)',
  },
};

type ComboboxProps = {
  options: DefaultOptionType[];
  selectedItems: DefaultOptionType[];
  isLoading: boolean;
  inputValue: string;
  placeholder?: string;
  isRequired?: boolean;
  hasError?: boolean;
  isInPasteMode?: boolean;
  isDisabled?: boolean;
  isSingleSelect?: boolean;
  size?: ComboboxSize;
  onSelect: (items: MultiValueType<DefaultOptionType>) => void;
  onInputChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
};

const Combobox = ({
  options,
  selectedItems,
  isLoading,
  inputValue,
  placeholder,
  isRequired,
  hasError,
  isInPasteMode = false,
  isDisabled = false,
  isSingleSelect = false,
  size = 'medium',
  onSelect,
  onInputChange,
  onKeyDown,
}: ComboboxProps) => {
  const sizeStyles = COMBOBOX_SIZE_STYLES[size];

  const defaultComponents = {
    ClearIndicator,
    LoadingIndicator: () => {
      return <Spinner height="32px" width="100%" />;
    },
    MultiValue,
    Option,
    DropdownIndicator: null,
  };

  const getOptionColor = (
    provided: SystemStyleObject,
    isSelected: boolean,
    isFocused: boolean,
    type: 'background' | 'hover',
  ) => {
    if (isSelected && !isFocused) {
      return type === 'background'
        ? theme.colors['light-blue'][50]
        : 'rgba(0, 0, 0, 0.05)';
    }
    if (isFocused) {
      return 'rgba(0, 0, 0, 0.05)';
    }
    return provided.backgroundColor;
  };

  const [displayValue, setDisplayValue] = useState(inputValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setDisplayValue(inputValue);
  }, [inputValue]);

  return (
    <Select<DefaultOptionType, true>
      components={defaultComponents}
      tabSelectsValue={false}
      filterOption={null}
      isLoading={isLoading}
      closeMenuOnSelect={isSingleSelect}
      options={options}
      placeholder={placeholder}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        onInputChange('');
      }}
      inputValue={displayValue}
      value={selectedItems}
      isMulti
      isDisabled={isDisabled}
      isRequired={isRequired}
      getOptionValue={(option) => option.id}
      onKeyDown={onKeyDown}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length > 0 ? 'No results found' : 'Start typing to search'
      }
      // Menu opens only when the input is focused. Single-select closes
      // after picking an item; clearing the selection reopens on next focus.
      menuIsOpen={
        isFocused &&
        (isSingleSelect && selectedItems.length > 0
          ? false
          : options.length > 0)
      }
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 1000,
        }),
      }}
      onChange={(value) => {
        const selected =
          isSingleSelect && value.length > 1 ? value.slice(-1) : value;
        onSelect(selected);
        // Close the dropdown after picking a single-select item by clearing
        // the search input. menuIsOpen is driven by options.length, so
        // closeMenuOnSelect alone has no effect.
        if (isSingleSelect && selected.length > 0) {
          setDisplayValue('');
          onInputChange('');
        }
      }}
      onInputChange={(value, action) => {
        if (action.action === 'set-value') {
          setDisplayValue('');
        }
        if (!isInPasteMode && action.action === 'input-change') {
          onInputChange(value);
        } else if (isInPasteMode) {
          //when pasting, allow only numbers and special characters
          const filteredValue = value.replace(/[a-zA-Z]/g, '');
          onInputChange(filteredValue);
        }
      }}
      chakraStyles={{
        option: (provided, state) => {
          return {
            ...provided,
            ':hover': {
              backgroundColor: getOptionColor(
                provided,
                state.isSelected,
                state.isFocused,
                'hover',
              ),
            },
            backgroundColor: getOptionColor(
              provided,
              state.isSelected,
              state.isFocused,
              'background',
            ),
            color: 'slate.900',
            borderRadius: 'var(--ds-layout-radius-100)',
            padding: 'var(--ds-layout-spacing-100) var(--ds-layout-spacing-75)',
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            opacity: state.isDisabled ? '0.5' : '1',
            pointerEvents: 'auto',
          };
        },
        valueContainer: (provided) => {
          return {
            ...provided,
            overflowY: 'auto',
            padding: sizeStyles.padding,
            display: 'flex',
            alignSelf: 'center',
            gap: '4px',
            width: '100%',
          };
        },
        placeholder: (provided, state) => {
          return {
            ...provided,
            color: 'var(--ds-color-content-subtle)',
            display: state.isFocused ? 'none' : 'block',
            height: '100%',
            verticalAlign: 'center',
          };
        },
        input: (provided) => {
          return {
            ...provided,
            color: 'var(--ds-color-content-default)',
          };
        },
        groupHeading: (provided) => {
          return {
            ...provided,
            color: 'slate.900',
            fontSize: '16px',
            fontWeight: '450',
          };
        },
        menu: (provided) => {
          return {
            ...provided,
            marginTop: 'var(--ds-layout-spacing-25)',
            borderRadius: 'var(--ds-layout-radius-150)',
            border: 'none',
            boxShadow:
              'var(--ds-misc-elevation-md-key-x) var(--ds-misc-elevation-md-key-y) var(--ds-misc-elevation-md-key-blur) var(--ds-misc-elevation-md-key-spread) var(--ds-misc-elevation-color-key), var(--ds-misc-elevation-md-ambient-x) var(--ds-misc-elevation-md-ambient-y) var(--ds-misc-elevation-md-ambient-blur) var(--ds-misc-elevation-md-ambient-spread) var(--ds-misc-elevation-color-ambient)',
            overflow: 'hidden',
          };
        },
        menuList: (provided) => {
          return {
            ...provided,
            border: 'none',
            padding: 'var(--ds-layout-spacing-75)',
          };
        },
        control: (provided, state) => {
          const strokeShadow = (
            color: string,
            width = 'var(--ds-layout-stroke-width-10)',
          ) => `inset 0 0 0 ${width} ${color}`;
          const focusShadow =
            'inset 0 0 0 var(--ds-layout-stroke-width-25) var(--ds-color-interactive-primary-enabled), 0 0 0 var(--ds-layout-stroke-width-50) var(--ds-color-interactive-primary-subtle-selected)';
          const restingShadow = hasError
            ? strokeShadow('var(--ds-color-support-negative-default)')
            : strokeShadow('var(--ds-color-stroke-default)');
          const hoverShadow = hasError
            ? strokeShadow('var(--ds-color-support-negative-default)')
            : strokeShadow('var(--ds-color-stroke-strong)');

          return {
            ...provided,
            alignItems: 'start',
            cursor: state.isDisabled ? 'not-allowed' : 'text',
            backgroundColor:
              'var(--ds-color-surface-background-level-1-default)',
            border: 'none',
            borderRadius: sizeStyles.borderRadius,
            minHeight: sizeStyles.minHeight,
            maxHeight: '120px',
            overflowY: 'auto',
            transition: 'box-shadow .11s cubic-bezier(.25, .1, .25, 1)',
            boxShadow: state.isFocused ? focusShadow : restingShadow,
            _hover: {
              boxShadow: state.isFocused ? focusShadow : hoverShadow,
            },
            _focus: {
              boxShadow: focusShadow,
            },
            _focusVisible: {
              boxShadow: focusShadow,
            },
          };
        },
      }}
    />
  );
};
export default Combobox;
